import { Link } from 'react-router-dom';
import { shortDate } from '../lib/format';

/**
 * One article, as a card. Shared by the hub, the category pages and the
 * related-articles row, so an article looks the same wherever it is met.
 */

export interface ArticleCardData {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  author_name: string;
  author_title: string | null;
  kind: 'editorial' | 'sponsored' | 'story';
  sponsor_name: string | null;
  tags: string[];
  cover_image_id: string | null;
  reading_minutes: number;
  view_count: number;
  is_featured: boolean;
  published_at: string | null;
  category_slug: string | null;
  category_name: string | null;
}

export const coverUrl = (id: string) => `/api/v1/content/images/${id}`;

/**
 * The label that keeps section 5's promise.
 *
 * Sponsored content must be distinguishable from editorial wherever it appears
 * — not only on the article itself — so it is rendered from the row's own kind
 * rather than left to each surface to remember.
 */
export function KindBadge({ kind, sponsor }: { kind: string; sponsor?: string | null }) {
  if (kind === 'sponsored') {
    return (
      <span className="ins-badge sponsored">
        Sponsored{sponsor ? ` · ${sponsor}` : ''}
      </span>
    );
  }
  if (kind === 'story') return <span className="ins-badge story">Reader story</span>;
  return null;
}

export function ArticleCard({ article: a, wide = false }: { article: ArticleCardData; wide?: boolean }) {
  return (
    <Link to={`/blog/${a.slug}`} className={`ins-card${wide ? ' wide' : ''}`}>
      <div className="ins-card-cover">
        {a.cover_image_id
          ? <img src={coverUrl(a.cover_image_id)} alt="" loading="lazy" />
          : <div className="ins-card-nocover" aria-hidden><img src="/brand-mark-64.png?v=2" alt="" width={44} height={44} /></div>}
      </div>
      <div className="ins-card-body">
        <p className="ins-card-meta">
          {a.category_name && <span className="ins-cat">{a.category_name}</span>}
          <KindBadge kind={a.kind} sponsor={a.sponsor_name} />
        </p>
        <h3 className="ins-card-title">{a.title}</h3>
        {a.summary && <p className="ins-card-sum">{a.summary}</p>}
        <p className="ins-card-foot">
          <span className="ins-av" aria-hidden>{a.author_name.trim().charAt(0).toUpperCase()}</span>
          <span className="ins-card-by">{a.author_name}</span>
          {a.published_at && <span>{shortDate(a.published_at)}</span>}
          <span>{a.reading_minutes} min read</span>
        </p>
      </div>
    </Link>
  );
}
